import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { environment } from "../../environments/environment";
import { Meta, Title } from "@angular/platform-browser";
import domainData from '../../assets/json/environment.json';
import ecommerceByDomain from '../../assets/json/ecommerce.json';
import { DOCUMENT } from '@angular/common';
import { AffiliateConfigService } from './affiliate-config.service';

interface MovingSizes { 1: string; 2: string; 3: string; 4: string }
interface DestinationTypes {
	1: {fullName: string, keyName: string},
	2: {fullName: string, keyName: string},
}
interface ConsentCookie {
	necessary: string,
	preference: string,
	statistics: string,
	advertising: string
}

/** Contact details from the form, passed to the purchase push for Enhanced Conversions. */
export interface SubmitUserData {
	email: string | null;
	phone: string | null;
}

export interface SubmitEmbedInfo {
	formToken: string;
}

/** GA ecommerce values per domain in assets/json/ecommerce.json (see GET .../super-form/v1/ecommerce). */
interface DomainEcommerceJson {
	ecommerce_price_int_1?: string;
	ecommerce_price_int_2?: string;
	ecommerce_price_int_3?: string;
	ecommerce_price_int_4?: string;
	ecommerce_price_nat_1?: string;
	ecommerce_price_nat_2?: string;
	ecommerce_price_nat_3?: string;
	ecommerce_price_nat_4?: string;
	individual_ecommerce_prices?: boolean;
	utm_ecommerce?: boolean;
	utm_prices?: Record<string, Record<string, string | number>>;
}
declare let ga: Function | undefined;
declare let gtag: Function | undefined;
declare let uetq: any | undefined;
declare var dataLayer: any[];
declare let fbq: Function | undefined;
declare let mixpanel: { init: ( token: string, opts?: object ) => void; track: ( name: string, props?: object ) => void } | undefined;

@Injectable( {
	providedIn: 'root'
} )
export class TrackingService {

	private submitHandled : boolean = false;
	private analyticsDebug : boolean = false;
	private ga4Debug : boolean = false;
	private consentCookieName : string = 'cookieaccepted';
	private consentCookieContent : ConsentCookie | undefined;
	public gtmLoaded = false;

	// Get moving sizes
	public movingSizes : MovingSizes = {
		1: 'Complete household',
		2: 'Part of household',
		3: 'Few pieces of furniture',
		4: 'Some boxes or luggage',
	};
	private destinationTypes : DestinationTypes = {
		1: {
			fullName: 'International',
			keyName: 'int',
		},
		2: {
			fullName: 'National',
			keyName: 'nat',
		},
	}

	private domainData: any;
	private ecommerceConfig: DomainEcommerceJson | undefined;

	constructor(
		private titleService: Title,
		private metaService: Meta,
		@Inject(DOCUMENT) private doc: any,
		@Inject(LOCALE_ID) public locale: string,
		private affiliateConfigService: AffiliateConfigService
	) {

		// Set canonical tag to itself without parameters
		this.setCanonical();

		// Per-host config from environment.json (not GET /settings)
		let hostname = window.location.hostname;
		let domain = hostname.replace( 'www.', '' );

		if( ! environment.production && ! environment.staging ) {

			domain = 'sea.localhost';
		}

		this.domainData = this.affiliateConfigService.config ?? domainData[domain as keyof typeof domainData];
		this.ecommerceConfig = ( ecommerceByDomain as Record<string, DomainEcommerceJson> )[domain];

		if( typeof this.domainData?.metaTitle === 'string' && this.domainData.metaTitle.length > 0 ) {

			this.titleService.setTitle( this.domainData.metaTitle );
		}
		else {

			this.titleService.setTitle( this.getMetaTitle() );
		}

		if( typeof this.domainData?.metaDesc === 'string' && this.domainData.metaDesc.length > 0 ) {

			this.metaService.addTags([
				{name: 'description', 'content': this.domainData.metaDesc}
			] );
		}

		var gaUaId = this.domainData?.gtagId;
		var ga4Id = this.domainData?.ga4Id;
		var bingId = this.domainData?.bingId;
		var hotjarId = this.domainData?.hotjarId;
		var gtmId = this.domainData?.gtmId;
		var facebookPixelId = this.domainData?.facebookPixelId;
		var mixpanelToken = environment.mixpanelToken;
		var clarityEnabled = this.domainData?.clarityEnabled;
		var clarityToken = environment.clarityToken;

		// Read out consent cookie
		if( this.consentCookieExists() ) {

			this.consentCookieContent = this.consentReadCookie() as ConsentCookie;
		}
		else {

			this.consentCookieContent = {
				necessary: 'false',
				preference: 'false',
				statistics: 'false',
				advertising: 'false',
			};
		}

		// Consent default has to be set before any Google tag (gtag.js or GTM) loads
		this.setConsentDefault();

		this.loadGa4( ga4Id );

		if( this.consentCookieExists() ) {

			console.log( this.consentCookieContent );
			console.log( this.domainData );

			// Need Statistics
			if( gaUaId && gaUaId.length > 0 && this.consentCookieContent.statistics ) {

				this.consentLoadScript( "( function( i, s, o, g, r, a, m ) { i['GoogleAnalyticsObject'] = r; i[r] = i[r] || function() { ( i[r].q = i[r].q || [] ).push( arguments ) }, i[r].l = 1 * new Date(); a = s.createElement( o ), m = s.getElementsByTagName( o )[0]; a.async = 1; a.src = g; m.parentNode.insertBefore( a, m ) } )( window, document, 'script', 'https://www.google-analytics.com/analytics" + (this.analyticsDebug ? "_debug" : "") + ".js', 'ga' ); ga( 'create', '" + gaUaId + "', 'auto' ); ga( 'require', 'ec' );" )
			}

			// Need Statistics & Advertising
			// Only on production
			if( hotjarId && hotjarId.length > 0 && this.consentCookieContent.statistics && this.consentCookieContent.advertising && environment.production ) {

				this.consentLoadScript( "(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:" + hotjarId + ",hjsv:5};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'//static.hotjar.com/c/hotjar-','.js?sv=');" );
			}
		}

		// Load GTM
		this.loadGtm( gtmId );

		// Load bing
		this.loadBing( bingId );

		// Load Facebook Pixel
		this.loadFacebookPixel( facebookPixelId );

		// Load Mixpanel (when token is set and user has consented to statistics)
		if( mixpanelToken && mixpanelToken.length > 0 && this.consentCookieExists() && this.consentCookieContent?.statistics ) {

			this.loadMixpanel( mixpanelToken );
		}

		// Load Microsoft Clarity (when enabled)
		if( clarityEnabled && clarityToken && clarityToken.length > 0 ) {

			this.loadClarity( clarityToken );
		}
	}

	public clarityEvent( eventName: string ): void {

		if( typeof window !== 'undefined' && (window as any).clarity ) {

			(window as any).clarity( 'event', eventName );
		}
	}

	/**
	 * Track an event. Step is always included in the payload.
	 * Replace the implementation in this method to switch to another analytics provider.
	 */
	public trackEvent( eventName: string, step: string, properties?: Record<string, unknown> ): void {

		const payload = { step, ...properties };

		if( typeof mixpanel !== 'undefined' && mixpanel.track ) {

			mixpanel.track( eventName, payload );
		}
	}

	public consentLoadScript( $content: string ) {

		var $script = document.createElement( 'script' );
		$script.innerHTML = $content;
		document.head.append( $script );
	}

	public setStepWithName( stepName:string ) {

		// On test and local only
		var logTracking = true;

		// Prepare the pageview (GTM or GA4)
		var pageViewData = {
			page_title: 'Form-' + stepName,
			page_location: document.location.origin + '/quote-form/' + stepName,
		}

		// Attempt to do this with GTM
		if( this.gtmLoaded ) {

			this.sendGTMEvent( {
				event: 'page_view',
				page_title: pageViewData.page_title,
				page_location: pageViewData.page_location
			} )

			if( logTracking ) {

				console.table( pageViewData )
			}
		}
		// Otherwise directly with GA4
		else if( typeof gtag !== 'undefined' ) {

			gtag( 'event', 'page_view', pageViewData );

			if( logTracking ) {

				console.table( pageViewData )
			}
		}

	}

	public onSubmitForm( token: string, movingSize: number, destinationType: number, paramCa?: string, userData?: SubmitUserData, embed?: SubmitEmbedInfo ) {

		// If already done
		if( this.submitHandled ) {

			return;
		}

		this.submitHandled = true;

		// The visitor may have answered the cookie banner after this form (iframe) was loaded
		this.updateConsentFromCookie();

		if( this.gtmLoaded ) {

			this.onSubmitGtm( token, movingSize, destinationType, paramCa, userData );
		}
		else {

			this.onSubmitGa( token, movingSize, destinationType, paramCa );
		}

		this.onSubmitBing();
		this.onSubmitFacebookPixel();
		this.onSubmitNotifyParent( movingSize, destinationType, embed );
		this.setStepWithName( 'thank-you' );
	}

	private onSubmitNotifyParent( movingSize: number, destinationType: number, embed?: SubmitEmbedInfo ) {

		if( ! embed || window.parent === window ) {

			return;
		}

		window.parent.postMessage( {
			event: 'sirelo:leadSubmitted',
			formToken: embed.formToken,
			movingSize: movingSize,
			destinationType: destinationType === 1 ? 'international' : 'domestic',
		}, '*' );
	}

	private getEcommercePriceForKey( priceKey: string ): string | number {

		const ec = this.ecommerceConfig as Record<string, unknown> | undefined;
		if( ! ec ) {

			return '';
		}

		const raw = ec[priceKey];
		if( raw === undefined || raw === null ) {

			return '';
		}

		if( typeof raw === 'boolean' ) {

			return '';
		}

		return raw as string | number;
	}

	/**
	 * Price and product name for the purchase, shared by the GA and GTM routes.
	 */
	private getPurchaseData( movingSize: number, destinationType: number, paramCa?: string ): { price: string | number, name: string } {

		// Build key
		var $priceKey = 'ecommerce_price_int_' + movingSize;

		// Get price from per-domain ecommerce config (ecommerce.json)
		// By not-individual we take int as a default
		// ecommerce_price_int_{1-4}
		var $price = this.getEcommercePriceForKey( $priceKey );

		// Product name based on moving size
		var $name = this.movingSizes[movingSize as keyof MovingSizes];

		// Results in complete name of moving size
		var $destinationTypeName = this.destinationTypes[destinationType as keyof DestinationTypes].fullName;

		// Results in the key name {int/nat}
		var $destinationTypeKeyName = this.destinationTypes[destinationType as keyof DestinationTypes].keyName;

		// If this site has individual pricing for int and nat
		if( this.ecommerceConfig?.individual_ecommerce_prices ) {

			// Overwrite price key to take dest into account
			$priceKey = 'ecommerce_price_' + $destinationTypeKeyName + '_' + movingSize;

			// Overwrite price with new priceKey
			$price = this.getEcommercePriceForKey( $priceKey );

			// Add to name
			$name += ' (' + $destinationTypeName + ')';
		}

		// If ecommerce prices based on utm is enabled and if there is a parameter
		if( this.ecommerceConfig?.utm_ecommerce && paramCa ) {

			// If prices exist for this parameter (value from CA)
			const utmBlock = this.ecommerceConfig?.utm_prices?.[paramCa];

			if( utmBlock ) {

				$priceKey = 'ecommerce_price_' + $destinationTypeKeyName + '_' + movingSize;
				var $newPrice = utmBlock[$priceKey];

				// Failsafe
				if( $newPrice !== undefined && $newPrice !== null && Number( $newPrice ) > 0 ) {

					$price = $newPrice;
				}
			}
		}

		return { price: $price, name: $name };
	}

	private onSubmitGa( token: string, movingSize: number, destinationType: number, paramCa?: string ) {

		const { price: $price, name: $name } = this.getPurchaseData( movingSize, destinationType, paramCa );

		if( ! environment.production ) {

			console.log( {ecommerce: 'started', price: $price, name: $name} );
		}

		if( typeof ga !== 'undefined' ) {

			// Start ecommerce
			ga( 'require', 'ecommerce' )

			// Add the transaction
			ga( 'ecommerce:addTransaction', {
				'id': token,
				'revenue': $price,
			} );

			// Add the item
			ga( 'ecommerce:addItem', {
				'id': token,
				'name': $name,
				'price': $price,
				'quantity': 1,
			} );

			// Send the data
			ga( 'ecommerce:send' );

			// Send final page view
			ga( 'send', 'pageview', '/quote-form/finished' );
		}

		if( typeof gtag !== 'undefined' ) {

			var purchaseEvent = {
				transaction_id: token,
				currency: 'EUR',
				value: $price,
				items: [
					{
						item_id: token,
						item_name: $name,
						price: $price,
						quantity: 1,
					}
				]
			}

			if( ! environment.production ) {

				console.log( purchaseEvent );
			}

			gtag( 'event', 'purchase', purchaseEvent );
		}
	}

	private onSubmitBing() {

		// Do bing event
		if( this.domainData?.bingId && this.domainData?.bingId.length > 0 && environment.production ) {

			if( typeof uetq !== 'undefined' ) {

				uetq.push('event', 'submit', {"event_category":"leads"});
			}
		}
	}

	private onSubmitFacebookPixel() {

		// Do Facebook Pixel event
		if( this.domainData?.facebookPixelId && this.domainData?.facebookPixelId.length > 0 ) {

			if( typeof fbq !== 'undefined' ) {

				fbq('track', 'Lead');
			}
		}
	}

	private onSubmitGtm( token: string, movingSize: number, destinationType: number, paramCa?: string, userData?: SubmitUserData ) {

		if( typeof dataLayer !== 'undefined' ) {

			dataLayer.push( {
				'event': 'ThankYouPageView',
				'virtualPageURL': '/thank-you'
			} );

			const { price, name } = this.getPurchaseData( movingSize, destinationType, paramCa );

			// GA4 ecommerce format. Clear the previous ecommerce object first so values never carry over.
			dataLayer.push( { ecommerce: null } );

			const enhancedConversionData = this.getEnhancedConversionData( userData );

			dataLayer.push( {
				event: 'purchase',
				...enhancedConversionData,
				ecommerce: {
					transaction_id: token,
					currency: 'EUR',
					value: Number( price ) || 0,
					items: [
						{
							item_id: token,
							item_name: name,
							price: Number( price ) || 0,
							quantity: 1,
						}
					]
				}
			} );

			// GTM's data model is sticky: clear the user data from it so later events on this page never see it.
			// (The purchase object itself stays in the window.dataLayer array.)
			if( enhancedConversionData.user_data ) {

				dataLayer.push( { user_data: null } );
			}
		}
	}

	/**
	 * User-provided data (email, phone) for Google Ads Enhanced Conversions for leads. GTM hashes the
	 * values in the Ads tag. Offline conversions are uploaded from the ERP and match on gclid; this is
	 * the on-site half of a fallback match for leads without a gclid, which only works once the ERP
	 * also exports those leads with a hashed email/phone (follow-up to DEV-7671).
	 * Only returned when the visitor granted advertising consent (same signal as ad_user_data);
	 * otherwise nothing is added, so no personal data reaches the dataLayer.
	 * Only used on the GTM path; sites that load GA4 directly (onSubmitGa) do not send it.
	 */
	private getEnhancedConversionData( userData?: SubmitUserData ): { user_data?: { email?: string, phone_number?: string } } {

		if( ! userData || this.getConsentState( this.consentCookieContent ).ad_user_data !== 'granted' ) {

			return {};
		}

		const email = ( userData.email ?? '' ).trim().toLowerCase();

		// Google needs the country code, so only the E.164 form (+31...) is usable
		// Without the intl-tel-input utils the raw input is passed on, so drop a trunk "(0)" before stripping
		const phone = ( userData.phone ?? '' ).replace( /\(0\)/g, '' ).replace( /[^\d+]/g, '' );
		const phoneNumber = /^\+[1-9]\d{6,14}$/.test( phone ) ? phone : '';

		if( email === '' && phoneNumber === '' ) {

			return {};
		}

		return {
			user_data: {
				...( email !== '' && { email } ),
				...( phoneNumber !== '' && { phone_number: phoneNumber } ),
			}
		};
	}

	private consentCookieExists() {

		return ( document.cookie.indexOf( this.consentCookieName + '=' ) >= 0 );
	}

	private consentReadCookie() {

		var $ca = document.cookie.split( ';' );
		for( var i = 0; i < $ca.length; i ++ ) {
			var c = $ca[i];
			while( c.charAt( 0 ) == ' ' ) c = c.substring( 1, c.length );
			if( c.indexOf( this.consentCookieName + '=' ) == 0 ) {
				try {
					return JSON.parse( c.substring( ( this.consentCookieName + '=' ).length, c.length ) );
				} catch (e) {
					return false;
				}
			}
		}
		return false;
	}

	private getMetaTitle(): string {

		// Title per language
		if( this.locale == 'en-US' ) {

			return 'Get quotes';
		}
		else if( this.locale == 'en-GB' ) {

		}
		else if( this.locale == 'nl' || this.locale == 'nl-BE' ) {

			return 'Offertes aanvragen';
		}
		else if( this.locale == 'de' ) {

			return 'Angebot anfragen';
		}
		else if( this.locale == 'it' ) {

			return 'Richiedi Preventivo';
		}
		else if( this.locale == 'es' ) {

			return 'Solicita presupuestos';
		}
		else if( this.locale == 'fr' ) {

			return 'Demande de devis';
		}

		return 'Get quotes';
	}

	private setCanonical() {

		// Set canonical
		const currentUrlWithoutParams = window.location.origin + window.location.pathname;

		let link: HTMLLinkElement = this.doc.createElement('link');
		link.setAttribute('rel', 'canonical');
		this.doc.head.appendChild(link);
		link.setAttribute('href', currentUrlWithoutParams );
	}

	/**
	 * Google Consent Mode default, based on the consent cookie. Set for every domain (also without a
	 * GA4 ID, e.g. when GA4 runs via GTM) and before any Google tag loads.
	 * statistics -> analytics_storage, advertising -> ad_*, preference -> functionality/personalization.
	 */
	private setConsentDefault() {

		const consentDefault = this.getConsentState( this.consentCookieContent );

		this.consentLoadScript( "window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('consent', 'default', " + JSON.stringify( consentDefault ) + ");" );
	}

	/**
	 * Re-read the consent cookie and send a Consent Mode update, so a choice made after this
	 * iframe was loaded is used for the conversion. Also stores the choices in consentCookieContent
	 * (undefined without a valid cookie, which reads as denied), which getEnhancedConversionData()
	 * relies on, so call this before onSubmitGtm().
	 */
	private updateConsentFromCookie() {

		const choices = this.consentCookieExists() ? this.consentReadCookie() : null;
		const consentUpdate = this.getConsentState( choices );

		// Keep the latest choices, so anything that needs consent after the form was loaded uses the same cookie.
		this.consentCookieContent = choices ? choices as ConsentCookie : undefined;

		if( typeof gtag !== 'undefined' ) {

			gtag( 'consent', 'update', consentUpdate );
		}

		if( typeof uetq !== 'undefined' ) {

			uetq.push( 'consent', 'update', { ad_storage: consentUpdate.ad_storage } );
		}
	}

	/**
	 * Single mapping from the consent cookie to Consent Mode signals, used for the default and the update.
	 * Missing or invalid cookie: everything denied except security_storage.
	 */
	private getConsentState( choices: Partial<ConsentCookie> | false | null | undefined ) {

		const granted = ( value: unknown ): string => ( value === true || value === 'true' ) ? 'granted' : 'denied';
		const c: Partial<ConsentCookie> = choices || {};

		return {
			analytics_storage: granted( c.statistics ),
			ad_storage: granted( c.advertising ),
			ad_user_data: granted( c.advertising ),
			ad_personalization: granted( c.advertising ),
			functionality_storage: granted( c.preference ),
			personalization_storage: granted( c.preference ),
			security_storage: 'granted',
		};
	}

	private loadGa4( ga4Id: string ) {

		// GA4
		if( ga4Id && ga4Id.length > 0 ) {

			var tagManager = document.createElement("script");
			tagManager.type = "text/javascript";
			tagManager.setAttribute("async", "true");
			tagManager.setAttribute("src", "https://www.googletagmanager.com/gtag/js?id=" + ga4Id );

			// Checking if there is a first child
			if( document.documentElement.firstChild ) {

				document.documentElement.firstChild.appendChild(tagManager);
			}

			this.consentLoadScript("window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '"+ga4Id+"'"+(this.ga4Debug ? ", { 'debug_mode':true }" : "" ) + ");");
		}
	}

	private loadGtm( gtmId: string ) {

		if( gtmId && gtmId.length > 0 ) {

			// Load outside of consent for now
			this.consentLoadScript("(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','" + gtmId + "');");

			this.gtmLoaded = true;
		}
	}

	private loadBing( bingId: string ) {

		if( bingId && bingId.length > 0 ) {

			this.consentLoadScript(
				"window.uetq = window.uetq || [];" +
				"var bingConsentDataDefault = {" +
				"'ad_storage': " +
				"(" + this.consentCookieExists() + " && " + this.consentCookieContent?.advertising +
				" ? 'granted' : 'denied')" +
				"};" +
				"window.uetq.push('consent', 'default', bingConsentDataDefault);"
			);

			this.consentLoadScript( '(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"' +  bingId + '"};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");' );
		}
	}

	private loadFacebookPixel( facebookPixelId: string ) {

		if( facebookPixelId && facebookPixelId.length > 0 ) {

			this.consentLoadScript(
				"!function(f,b,e,v,n,t,s)" +
				"{if(f.fbq)return;n=f.fbq=function(){n.callMethod?" +
				"n.callMethod.apply(n,arguments):n.queue.push(arguments)};" +
				"if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';" +
				"n.queue=[];t=b.createElement(e);t.async=!0;" +
				"t.src=v;s=b.getElementsByTagName(e)[0];" +
				"s.parentNode.insertBefore(t,s)}(window, document,'script'," +
				"'https://connect.facebook.net/en_US/fbevents.js');" +
				"fbq('init', '" + facebookPixelId + "');" +
				"fbq('track', 'PageView');"
			);
		}
	}

	private loadMixpanel( mixpanelToken: string ): void {

		if( !mixpanelToken || mixpanelToken.length === 0 ) {

			return;
		}

		// Official Mixpanel snippet: loader stub + async lib load, then init with EU api_host and options.
		// Uses https URL directly (no protocol-relative check needed for web apps).
		const loader = "(function(e,c){if(!c.__SV){var l,h;window.mixpanel=c;c._i=[];c.init=function(q,r,f){function t(d,a){var g=a.split(\".\");2==g.length&&(d=d[g[0]],a=g[1]);d[a]=function(){d.push([a].concat(Array.prototype.slice.call(arguments,0)))}}var b=c;\"undefined\"!==typeof f?b=c[f]=[]:f=\"mixpanel\";b.people=b.people||[];b.toString=function(d){var a=\"mixpanel\";\"mixpanel\"!==f&&(a+=\".\"+f);d||(a+=\" (stub)\");return a};b.people.toString=function(){return b.toString(1)+\".people (stub)\"};l=\"disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders start_session_recording stop_session_recording people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove\".split(\" \");for(h=0;h<l.length;h++)t(b,l[h]);var n=\"set set_once union unset remove delete\".split(\" \");b.get_group=function(){function d(p){a[p]=function(){b.push([g,[p].concat(Array.prototype.slice.call(arguments,0))])}}for(var a={},g=[\"get_group\"].concat(Array.prototype.slice.call(arguments,0)),m=0;m<n.length;m++)d(n[m]);return a};c._i.push([q,r,f])};c.__SV=1.2;var k=e.createElement(\"script\");k.type=\"text/javascript\";k.async=!0;k.src=\"undefined\"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:\"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js\";e=e.getElementsByTagName(\"script\")[0];e.parentNode.insertBefore(k,e)}})(document,window.mixpanel||[]);";
		const recordSessionsPercent = this.domainData?.mixpanelRecordSessions === true ? 100 : 0;
		const initOptions = "{ autocapture: false, record_sessions_percent: " + recordSessionsPercent + ", api_host: 'https://api-eu.mixpanel.com' }";
		this.consentLoadScript( loader + " mixpanel.init('" + mixpanelToken + "', " + initOptions + ");" );
	}

	public sendGTMEvent( event: any ) {

		console.log( 'sending tracking event' );

		if (typeof dataLayer !== 'undefined') {
			dataLayer.push( event );
		}
	}

	private loadClarity( clarityToken: string ): void {

		if( !clarityToken || clarityToken.length === 0 ) {

			return;
		}

		// Load Microsoft Clarity script
		this.consentLoadScript(
			"(function(c,l,a,r,i,t,y){" +
			"c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};" +
			"t=l.createElement(r);t.async=1;t.src=\"https://www.clarity.ms/tag/\"+i;" +
			"y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);" +
			"})(window, document, \"clarity\", \"script\", \"" + clarityToken + "\");"
		);

		// Send specific domain configuration as custom data
		setTimeout(() => {

			if( typeof window !== 'undefined' && (window as any).clarity && this.domainData ) {

				// Define which config options to send to Clarity
				const configsToSend = [
					'residenceEnabled',
					'propertyRoomsEnabled',
					'movingToExtendedEnabled',
					'enableGooglePlacesAutocomplete',
					'm2FieldEnabled',
					'preMatchEnabled',
					'verificationEnabled',
					'baggagePhotosEnabled'
				];

				// Send each config as a separate root-level custom data property (only when true)
				configsToSend.forEach( configKey => {

					if( this.domainData[configKey] === true ) {

						(window as any).clarity( 'set', configKey, this.domainData[configKey] );
					}
				} );
			}
		}, 1000 );
	}
}
